import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { Button, P, Wrapper } from '@farewill/ui'
import { FONT, GTR } from '@farewill/ui/tokens'

import CircularRadioGroup from 'components/form/circular-radio-group'
import Input from 'components/form/input'
import { formatCurrency } from 'lib/formatting/currency'
import { hideModal } from 'state/actions'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
`

const StyledCloseButton = styled(Button)`
  font-weight: ${FONT.WEIGHT.REGULAR};
  text-decoration: underline;
  align-self: center;
  margin-top: ${GTR.XS};

  &:hover {
    text-decoration: underline;
  }
`

const StyledFieldsWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  gap: ${GTR.XS};
  padding: ${GTR.M};
`

const PAYER_TYPE = {
  purchaser: 'purchaser',
  thirdParty: 'third_party',
} as const

type PayerType = (typeof PAYER_TYPE)[keyof typeof PAYER_TYPE]

export type ThirdPartyPayer = {
  firstName: string
  lastName: string
  email: string
}

type FormValues = ThirdPartyPayer & { payerType: PayerType }

/** Only required when a third party is paying */
const requiredForThirdParty = (message: string) => ({
  is: PAYER_TYPE.thirdParty,
  then: yup.string().required(message),
  otherwise: yup.string(),
})

const VALIDATION_SCHEMA = yup.object().shape({
  firstName: yup
    .string()
    .when(
      'payerType',
      requiredForThirdParty('Please enter the payer’s first name')
    ),
  lastName: yup
    .string()
    .when(
      'payerType',
      requiredForThirdParty('Please enter the payer’s last name')
    ),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .when(
      'payerType',
      requiredForThirdParty('Please enter the payer’s email address')
    ),
})

type ModalProps = {
  config: {
    amountInPence: number
    openPciPalTab: (thirdPartyPayer?: ThirdPartyPayer) => Promise<void>
  }
}

const BalancePaymentModal = ({ config }: ModalProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [isPaymentAttempted, setIsPaymentAttempted] = useState(false)

  const handleSubmit = async ({
    payerType,
    firstName,
    lastName,
    email,
  }: FormValues) => {
    await config.openPciPalTab(
      payerType === PAYER_TYPE.thirdParty
        ? { firstName, lastName, email }
        : undefined
    )
    setIsPaymentAttempted(true)
  }

  const handleClose = () => {
    dispatch(hideModal())
    isPaymentAttempted && history.go(0) // Refresh page to re-retrieve funeral plan
  }

  return (
    <Wrapper>
      <Formik<FormValues>
        initialValues={{
          payerType: PAYER_TYPE.purchaser,
          firstName: '',
          lastName: '',
          email: '',
        }}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting }) => (
          <Form>
            {isPaymentAttempted ? (
              <>
                <P>
                  If you’ve successfully taken payment, click{' '}
                  <strong>I’ve taken payment</strong>.
                </P>
                <P>
                  If you did not successfully take payment, click{' '}
                  <strong>Take payment (8x8)</strong> to try again.
                </P>
              </>
            ) : (
              <>
                <P>
                  Click <strong>Take payment (8x8)</strong> to open a new tab.
                  The customer can enter their card details securely using their
                  phone keypad - no need to pause the call recording. Return
                  here when you’re done.
                </P>
                <CircularRadioGroup
                  label="Who is paying?"
                  name="payerType"
                  options={[
                    { value: PAYER_TYPE.purchaser, label: 'Plan purchaser' },
                    {
                      value: PAYER_TYPE.thirdParty,
                      label: 'Someone else',
                    },
                  ]}
                />
                {values.payerType === PAYER_TYPE.thirdParty && (
                  <StyledFieldsWrapper>
                    <P size="S">
                      We’ll pass these details to 8x8 so the payment is taken
                      against the person paying, not the plan purchaser.
                    </P>
                    <Input label="First name" name="firstName" />
                    <Input label="Last name" name="lastName" />
                    <Input label="Email" name="email" type="email" />
                  </StyledFieldsWrapper>
                )}
                <Wrapper centered padding={['S', '0', 'M']}>
                  <P data-testid="amount-to-charge" strong>
                    You will be charging the customer{' '}
                    {formatCurrency({
                      showPence: true,
                      value: config.amountInPence,
                      valueInPence: true,
                    })}
                  </P>
                </Wrapper>
              </>
            )}

            <StyledWrapper>
              <Button.Primary type="submit" loading={isSubmitting}>
                Take payment (8x8)
              </Button.Primary>
              <StyledCloseButton type="button" onClick={handleClose}>
                {isPaymentAttempted
                  ? 'I’ve taken payment'
                  : 'Close without taking payment'}
              </StyledCloseButton>
            </StyledWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

export default BalancePaymentModal
